import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { LoadState } from '../loadState';

export type PrincipleType = 'ATTACK' | 'DEFEND' | 'TRANSITION' | 'RESTART';

export const PRINCIPLE_TYPES: PrincipleType[] = ['ATTACK', 'DEFEND', 'TRANSITION', 'RESTART'];

export interface PrincipleRow {
  id: string;
  type: PrincipleType;
  text: string;
  corner_ids: string[];
  phase: string | null;
  created_at: string | null;
}

interface TeamPrinciplesResult {
  rows: PrincipleRow[];
  state: LoadState;
  errorMessage?: string;
  /** Re-fetch from the DB. Caller invokes after a save/delete to keep
   *  the list in sync without rolling its own optimistic state. */
  refresh: () => void;
}

/**
 * Read all live principles for a team. No offline fallback — principles
 * are coach-authored, so missing them on a fresh team is the "empty"
 * state, not an error to paper over.
 */
export function useTeamPrinciples(
  teamId: string | null | undefined,
  teamLoading: boolean,
): TeamPrinciplesResult {
  const [rows, setRows] = useState<PrincipleRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (teamLoading) {
      setState('loading');
      return;
    }
    if (!teamId) {
      setRows([]);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      const { data, error } = await supabase
        .from('principles')
        .select('id, type, text, corner_ids, phase, created_at')
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      if (error) {
        setErrorMessage(error.message);
        setState('error');
        return;
      }
      const list = (data ?? []) as PrincipleRow[];
      setRows(list);
      setState(list.length > 0 ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, teamLoading, refreshTick]);

  return {
    rows,
    state,
    errorMessage,
    refresh: () => setRefreshTick((n) => n + 1),
  };
}
